import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { from, map, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { RoleKey } from '@core/constants';
import { AlertsService } from '@services/AlertsSystem/alerts.service';
import { CRUDPrioritizedActivitiesService } from '@services/emergencies/CRUD-PrioritizedActivities.service';
import { RoleCatalogService } from '@services/roles/role-catalog.service';

/**
 * Guard de acceso al detalle de una actividad priorizada.
 *
 * La propiedad de una actividad la marca su **entidad responsable**
 * (`prio_acti_id_responsible_entity`), no quién la creó: desde que el ejecutor escoge la
 * entidad implementadora al crearla, el creador y el responsable casi nunca coinciden.
 *
 * Ejecutor, administrador y auditoría supervisan todas las entidades y pasan sin
 * comprobación. El implementador solo alcanza lo de su propia entidad.
 *
 * El backend aplica la misma regla y responde 403; este guard solo evita entrar a una
 * pantalla que iba a fallar.
 */
@Injectable({ providedIn: 'root' })
export class PermissionToActivityGuard implements CanActivate {
  private readonly _prioritizedActivityService = inject(CRUDPrioritizedActivitiesService);
  private readonly _router = inject(Router);
  private readonly _alertService = inject(AlertsService);
  private readonly _roleCatalog = inject(RoleCatalogService);

  /** Roles que supervisan todas las entidades implementadoras. */
  private readonly OVERSIGHT_ROLES: readonly RoleKey[] = ['EXECUTOR', 'ADMINISTRATOR', 'AUDITOR'];

  /**
   * Evalúa si el usuario puede abrir el detalle de la actividad solicitada.
   * @param route - Snapshot de la ruta a activar (debe incluir `params.idActivity`)
   * @param state - Estado actual del router, del que se extrae la emergencia
   * @returns Observable con `true` si tiene acceso, `false` tras redirigir al listado
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    // El catálogo ya viene cargado del arranque; load() solo reintenta si aquella carga falló.
    return from(this._roleCatalog.load()).pipe(
      switchMap(() => this.evaluateAccess(route, state)),
    );
  }

  /** Aplica la regla de acceso una vez resueltos los ids de rol del ambiente. */
  private evaluateAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    if (this._roleCatalog.isSessionRole(...this.OVERSIGHT_ROLES)) {
      return of(true);
    }

    const userEntityId = Number(localStorage.getItem('entity_id'));
    const activityId = Number(route.params['idActivity']);
    if (!Number.isFinite(activityId) || activityId <= 0) {
      return of(true);
    }

    return from(this._prioritizedActivityService.getActivityById(activityId)).pipe(
      map((response) => {
        const responsibleEntityId = Number(
          (response?.data as { prio_acti_id_responsible_entity?: number | null } | undefined)
            ?.prio_acti_id_responsible_entity ?? 0,
        );
        if (responsibleEntityId === userEntityId) {
          return true;
        }
        this.denyAccess(state.url);
        return false;
      }),
      // Un fallo al resolver la actividad no debe dejar la navegación colgada: se deniega
      // y la pantalla de origen queda visible.
      catchError(() => {
        this.denyAccess(state.url);
        return of(false);
      }),
    );
  }

  /** Avisa del bloqueo y devuelve al detalle de la emergencia. */
  private denyAccess(currentUrl: string): void {
    this._alertService.showError('Sin permiso', 'Esta actividad pertenece a otra entidad implementadora.');
    const segments = currentUrl.split('/');
    // La URL es `.../emergencies/:idEmergency/:idActivity`: se sube un nivel.
    void this._router.navigate([segments.slice(0, -1).join('/')]);
  }
}
